import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, X } from "lucide-react";
import { streamChat } from "@/lib/api";

const SEED = [
  {
    role: "assistant",
    content:
      "Hey — I'm Hero, HeroWebDesign's AI assistant. Ask me about services, pricing, timelines, or the process. What can I help with?",
  },
];

const SUGGESTIONS = [
  "What do you charge for a landing page?",
  "How long does a marketing site take?",
  "Do you also build the site or just design it?",
  "What's your process?",
];

export default function HeroChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(SEED);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: value }, { role: "assistant", content: "" }]);
    setSending(true);

    let assistantIdx;
    setMessages((m) => {
      assistantIdx = m.length - 1;
      return m;
    });

    await streamChat({
      sessionId,
      message: value,
      onSession: (sid) => setSessionId(sid),
      onDelta: (chunk) => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, content: last.content + chunk };
          }
          return next;
        });
      },
      onDone: () => setSending(false),
      onError: () => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && last.content === "") {
            next[next.length - 1] = {
              role: "assistant",
              content: "Sorry — I hit an error. Try again in a moment, or use the contact form.",
            };
          }
          return next;
        });
        setSending(false);
      },
    });
  };

  return (
    <>
      {/* Floating trigger */}
      <motion.button
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setOpen(true)}
        data-testid="chatbot-fab"
        className="fixed bottom-24 right-5 md:bottom-24 md:right-8 z-50 group"
        aria-label="Open Hero AI chat"
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-indigo-500/20 blur-xl animate-pulse" aria-hidden />
        <span className="relative inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 text-white px-4 md:px-5 py-3 shadow-lg ai-glow hover:scale-[1.03] transition-transform">
          <Sparkles size={16} />
          <span className="hidden md:inline text-sm font-medium">Ask Hero</span>
        </span>
      </motion.button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md bg-[#0a0a0a] border-l border-white/10 p-0 flex flex-col"
          data-testid="chatbot-sheet"
        >
          <SheetHeader className="px-5 py-4 border-b border-white/10 flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-700 grid place-items-center">
                  <Sparkles size={16} className="text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0a0a0a]" />
              </div>
              <div>
                <SheetTitle className="text-white font-display text-base">Hero</SheetTitle>
                <SheetDescription className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
                  AI · HeroWebDesign
                </SheetDescription>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-zinc-400 hover:text-white transition"
              aria-label="Close"
              data-testid="chatbot-close"
            >
              <X size={18} />
            </button>
          </SheetHeader>

          <div ref={listRef} className="flex-1 overflow-y-auto px-5">
            <div className="py-6 space-y-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chatbot-msg-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-white text-black"
                        : "bg-white/[0.04] border border-white/10 text-zinc-100"
                    }`}
                  >
                    {m.content || (
                      <span className="inline-flex gap-1 items-center text-zinc-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && (
                <div className="pt-4">
                  <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Try asking</p>
                  <div className="grid grid-cols-1 gap-2">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`chatbot-suggestion-${i}`}
                        onClick={() => send(s)}
                        className="text-left rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-sm text-zinc-200 px-4 py-3 transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-white/10 p-4 flex items-center gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, pricing, process..."
              data-testid="chatbot-input"
              className="flex-1 bg-white/[0.03] border border-white/10 focus:border-indigo-400 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none transition"
            />
            <Button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chatbot-send"
              className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-10 w-10 p-0 shrink-0"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
